/**
 * Collection grid: re-cut the server-truncated product titles to the pixel width actually available.
 *
 * The Liquid cut in snippets/product-card.liquid spends a character budget (17), but characters are not equal in
 * width, so one budget has to be safe for the widest title in the catalogue AND for the narrowest phone. Measured over
 * 32 tiles that costs 24px of unused line at 360px and 37px at 390px, and still overflows at 320px.
 *
 * This re-cuts every clipped title with the same four rules the Liquid uses, but against a measured pixel budget. The
 * server render stays as the fallback: without this file the grid looks exactly as it does today.
 */
(() => {
  'use strict';

  if (window.__josecoTitleFit) return;

  const ELLIPSIS = '…';
  const SAFETY_PX = 0.5; // absorbs the rounding between fractional layout and the integer clientWidth/scrollWidth pair
  const RESIZE_DEBOUNCE_MS = 150;
  const MAX_ROUNDS = 40;

  // The exact complement of the "min-width: 700px" rule that sets the clipped span to display:none. From there up the
  // tiles are wide enough, the full title is the one on screen, and every read this file would do is wasted work.
  const DESKTOP = matchMedia('(min-width: 700px)');

  const PROBE_STYLE_PROPS = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'fontStretch',
    'fontVariant',
    'fontKerning',
    'fontFeatureSettings',
    'fontVariationSettings',
    'letterSpacing',
    'wordSpacing',
    'textTransform',
    'textRendering',
  ];

  /**
   * Mirror of the Liquid cut, kept literally in step with snippets/product-card.liquid:
   *   1. slice to n characters, strip the surrounding whitespace
   *   2. drop a dangling "-", "," or "/" (never the "|", that one announces the colour)
   *   3. fewer than two colour characters after the last "|" reads as an accident, so fall back to the bare "|"
   *   4. append the ellipsis directly, never after a space
   *
   * Cutting happens on code points, not on UTF-16 units: slicing an emoji in half leaves a replacement glyph in front
   * of the ellipsis. The split is handed in rather than made here, because the binary search calls this once per round
   * and re-splitting the same title 8 times over is the single hottest allocation in the file.
   */
  const cutTitle = (title, points, n) => {
    if (n >= points.length) return title;

    let text = points.slice(0, n).join('').trim();

    if (text) {
      const last = text.slice(-1);
      if (last === '-' || last === ',' || last === '/') {
        text = text.slice(0, -1).trim();
      }
    }

    const parts = text.split('|');
    if (parts.length > 1 && parts[parts.length - 1].trim().length < 2) {
      text = parts.slice(0, -1).join('|') + '|';
    }

    // Liquid falls back to the whole title here and lets the CSS net catch it. We cannot: not overflowing is the whole
    // point. Only a title opening on whitespace or on a separator can land here.
    if (text === '') return ELLIPSIS;

    return text + ELLIPSIS;
  };

  let probeHost = null;
  const probes = [];

  const probeAt = (index, style) => {
    if (!probeHost) {
      probeHost = document.createElement('div');
      probeHost.setAttribute('aria-hidden', 'true');
      probeHost.style.cssText =
        'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;contain:layout style;z-index:-1;';
      document.body.appendChild(probeHost);
    }

    let probe = probes[index];

    if (!probe) {
      probe = document.createElement('span');
      probe.style.display = 'inline-block';
      probe.style.whiteSpace = 'pre';
      probe.appendChild(document.createTextNode(''));
      probeHost.appendChild(probe);
      probes[index] = probe;
    }

    for (const prop of PROBE_STYLE_PROPS) probe.style[prop] = style[prop];

    return probe;
  };

  // Writing through the text node rather than through textContent keeps these updates invisible to the observer below,
  // so re-cutting a title can never feed back into another run.
  const writeText = (element, text) => {
    const first = element.firstChild;

    if (first && first.nodeType === 3 && !first.nextSibling) {
      if (first.nodeValue !== text) first.nodeValue = text;
    } else {
      element.textContent = text;
    }
  };

  const fullTitleOf = (clipped, link) => {
    const full = link.querySelector('.product-title__full');
    const fromDom = full ? full.textContent.trim() : '';

    // Always start from the whole title, never from whatever a previous run left behind.
    return fromDom || (clipped.getAttribute('title') || '').trim();
  };

  const observedGrids = new WeakSet();
  const pending = new Set(); // cards added since the last pass, re-cut on their own without touching the rest of the grid
  let pendingFull = false;

  const observer = new MutationObserver((records) => {
    let dirty = false;

    for (const record of records) {
      // The layout switch swaps the attribute, which changes the tile width under titles that are already cut.
      if (record.type === 'attributes') {
        pendingFull = true;
        dirty = true;
        continue;
      }

      if (observedGrids.has(record.target)) {
        // Direct children of the grid only, so this is a tag comparison per node and never a subtree walk.
        for (const node of record.addedNodes) {
          if (node.tagName === 'PRODUCT-CARD') {
            pending.add(node);
            dirty = true;
          }
        }
      } else if (record.addedNodes.length) {
        // Section level: faceting and pagination hand back a whole new body, nothing finer is worth detecting.
        pendingFull = true;
        dirty = true;
      }
    }

    if (dirty) schedule();
  });

  const bind = () => {
    observer.disconnect();

    const sections = new Set();
    const grids = new Set();

    for (const clipped of document.querySelectorAll('.product-title__clipped')) {
      const grid = clipped.closest('product-list');
      if (grid) grids.add(grid);

      const section = clipped.closest('[id^="shopify-section-"]');
      if (section) sections.add(section);
    }

    // An empty result set has no tile to locate the grid from, but the section still has to be watched or clearing the
    // filter would come back to server-cut titles.
    if (!sections.size) {
      const collection = document.querySelector('.collection');
      const section = collection && collection.closest('[id^="shopify-section-"]');
      if (section) sections.add(section);
    }

    // Faceting replaces the children of the section, so the section is the stable node; the grid inside it is not.
    for (const section of sections) observer.observe(section, { childList: true });

    // childList without subtree: a wishlist button or a lazy image landing inside a card is invisible here, and that is
    // the whole point. Only cards arriving as direct children of the grid, and only the mobile layout attribute.
    for (const grid of grids) {
      observedGrids.add(grid);
      observer.observe(grid, { childList: true, attributes: true, attributeFilter: ['collection-mobile-layout'] });
    }
  };

  const clippedIn = (cards) => {
    const out = [];

    for (const card of cards) {
      if (!card.isConnected) continue;
      for (const clipped of card.querySelectorAll('.product-title__clipped')) out.push(clipped);
    }

    return out;
  };

  let runs = 0;
  let skips = 0;

  const run = (cards) => {
    if (DESKTOP.matches) {
      skips += 1;
      window.__josecoTitleFitDebug = { runs, skips, mode: 'skipped', cards: 0, layouts: 0, rounds: 0, lastMs: 0 };
      return;
    }

    const started = performance.now();
    const full = !cards;

    if (full) bind();

    const clippedNodes = full ? document.querySelectorAll('.product-title__clipped') : clippedIn(cards);

    if (!clippedNodes.length) {
      report(started, full, 0, 0);
      return;
    }

    // ---- read pass: one forced layout for the whole grid, nothing is written yet ----
    const groups = new Map(); // computed styles are read once per section, not once per tile
    const jobs = [];

    for (const clipped of clippedNodes) {
      const link = clipped.closest('.product-title');
      if (!link) continue;

      const section = clipped.closest('[id^="shopify-section-"]') || document.body;
      let group = groups.get(section);

      if (!group) {
        const linkStyle = getComputedStyle(link);
        const clippedStyle = getComputedStyle(clipped);
        const style = {};
        for (const prop of PROBE_STYLE_PROPS) style[prop] = clippedStyle[prop];

        group = {
          // From 700px up the clipped span is display:none, the tiles are wide enough and there is nothing to solve.
          active: clippedStyle.display !== 'none',
          style,
          chrome:
            parseFloat(linkStyle.paddingInlineStart) +
            parseFloat(linkStyle.paddingInlineEnd) +
            parseFloat(linkStyle.borderInlineStartWidth) +
            parseFloat(linkStyle.borderInlineEndWidth),
        };
        groups.set(section, group);
      }

      if (!group.active) continue;

      const budget = Math.min(link.getBoundingClientRect().width, link.clientWidth) - group.chrome - SAFETY_PX;
      if (!(budget > 0)) continue;

      const title = fullTitleOf(clipped, link);
      if (!title) continue;

      // The bound is the code point count, the same unit the cut spends. Counting UTF-16 units instead only bought
      // rounds that resolve to the whole title.
      const points = Array.from(title);

      jobs.push({ clipped, title, points, budget, style: group.style, lo: 1, hi: points.length, best: 0 });
    }

    let layouts = 1; // the read pass above, however many tiles it walked

    if (!jobs.length) {
      report(started, full, 0, layouts);
      return;
    }

    for (let i = 0; i < jobs.length; i += 1) jobs[i].probe = probeAt(i, jobs[i].style);

    // ---- binary search over the prefix length, every tile in lockstep ----
    // One write burst then one read burst per round, so the whole grid costs a single layout per round rather than
    // one per tile per candidate.
    let active = jobs;
    let rounds = 0;

    while (active.length && rounds < MAX_ROUNDS) {
      for (const job of active) {
        job.mid = (job.lo + job.hi) >> 1;
        job.probe.firstChild.nodeValue = cutTitle(job.title, job.points, job.mid);
      }

      layouts += 1;
      rounds += 1;

      const next = [];

      for (const job of active) {
        if (job.probe.getBoundingClientRect().width <= job.budget) {
          job.best = job.mid;
          job.lo = job.mid + 1;
        } else {
          job.hi = job.mid - 1;
        }

        if (job.lo <= job.hi) next.push(job);
      }

      active = next;
    }

    // ---- write pass ----
    for (const job of jobs) {
      writeText(job.clipped, job.best > 0 ? cutTitle(job.title, job.points, job.best) : ELLIPSIS);
    }

    report(started, full, jobs.length, layouts, rounds);
  };

  const report = (started, full, cards, layouts, rounds) => {
    runs += 1;
    window.__josecoTitleFitDebug = {
      runs,
      skips,
      mode: full ? 'full' : 'incremental',
      cards,
      layouts,
      rounds: rounds || 0,
      lastMs: Math.round((performance.now() - started) * 100) / 100,
    };
  };

  let scheduled = false;

  const flush = () => {
    scheduled = false;

    if (pendingFull || !pending.size) {
      pendingFull = false;
      pending.clear();
      run();
      return;
    }

    const cards = Array.from(pending);
    pending.clear();
    run(cards);
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(flush);
  };

  const scheduleFull = () => {
    pendingFull = true;
    schedule();
  };

  // Hiding and revealing the address bar fires resize on every scroll gesture on a phone, and none of those change the
  // width a title is cut to. Only a real width change is worth a pass.
  let lastWidth = document.documentElement.clientWidth;
  let resizeTimer = 0;

  const onResize = () => {
    const width = document.documentElement.clientWidth;
    if (width === lastWidth) return;

    lastWidth = width;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(scheduleFull, RESIZE_DEBOUNCE_MS);
  };

  window.__josecoTitleFit = { run: scheduleFull };

  // Measuring before the webfont lands would size every title against the fallback face.
  const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  fontsReady.then(() => run(), () => run());

  window.addEventListener('resize', onResize, { passive: true });
  document.addEventListener('shopify:section:load', scheduleFull);
})();
